import {
  assertOptionalFiniteNumber,
  validateUnitInterval
} from "./index-wxhvgv2s.mjs";
import {
  WEBGPU_SINGLE_PASS_VERTEX,
  setupWebGpuSinglePass,
  cleanupWebGpuSinglePass,
  applyWebGpuSinglePass
} from "./index-tvfexb7s.mjs";
import {
  assertEffectParamsObject
} from "./index-4fvp93ps.mjs";

// src/pixel-dissolve.ts
import { Internals } from "remotion";

// src/pixel-dissolve-webgpu-shader.ts
var PIXEL_DISSOLVE_WEBGPU_SHADER = `
${WEBGPU_SINGLE_PASS_VERTEX}
@group(0) @binding(0) var source: texture_2d<f32>;
@group(0) @binding(1) var sourceSampler: sampler;
@group(0) @binding(2) var<uniform> params: Params;

struct Params { progress: f32, columns: f32, rows: f32, seed: f32, feather: f32, }

fn hash21(p: vec2f) -> f32 {
  var p3 = fract(p.xyx * vec3f(443.8975, 397.2973, 491.1871));
  p3 += vec3f(dot(p3, p3.yzx + vec3f(19.19)));
  return fract((p3.x + p3.y) * p3.z);
}

@fragment fn fragment(input: VertexOutput) -> @location(0) vec4f {
  let divisions = max(vec2f(params.columns, params.rows), vec2f(1));
  let uv = vec2f(input.uv.x, 1.0 - input.uv.y);
  let cell = floor(min(uv, vec2f(1.0 - 0.000001)) * divisions + vec2f(0.00001));
  let color = textureSampleLevel(source, sourceSampler, input.uv, 0);
  let noise = hash21(cell + vec2f(params.seed, params.seed * 1.618));
  let edge = max(params.feather, 0.0);
  let threshold = 1.0 - clamp(params.progress, 0.0, 1.0) * (1.0 + edge);
  var mask: f32;
  if (edge <= 0.0001) { mask = 1.0 - step(threshold, noise); }
  else { mask = 1.0 - smoothstep(threshold, threshold + edge, noise); }
  return color * mask;
}

`;

// src/pixel-dissolve.ts
var { createEffect, createWebGL2ContextError } = Internals;
var DEFAULT_PROGRESS = 0.5;
var DEFAULT_COLUMNS = 10;
var DEFAULT_ROWS = 10;
var DEFAULT_SEED = 0;
var DEFAULT_FEATHER = 0.15;
var pixelDissolveSchema = {
  progress: {
    type: "number",
    min: 0,
    max: 1,
    step: 0.01,
    default: DEFAULT_PROGRESS,
    description: "Progress",
    hiddenFromList: false
  },
  columns: {
    type: "number",
    min: 1,
    max: 400,
    step: 1,
    integer: true,
    default: DEFAULT_COLUMNS,
    description: "Columns",
    hiddenFromList: false
  },
  rows: {
    type: "number",
    min: 1,
    max: 400,
    step: 1,
    integer: true,
    default: DEFAULT_ROWS,
    description: "Rows",
    hiddenFromList: false
  },
  seed: {
    type: "number",
    step: 1,
    default: DEFAULT_SEED,
    description: "Seed",
    hiddenFromList: false
  },
  feather: {
    type: "number",
    min: 0,
    max: 1,
    step: 0.01,
    default: DEFAULT_FEATHER,
    description: "Feather",
    hiddenFromList: false
  }
};
var resolve = (params) => ({
  progress: params.progress ?? DEFAULT_PROGRESS,
  columns: params.columns ?? DEFAULT_COLUMNS,
  rows: params.rows ?? DEFAULT_ROWS,
  seed: params.seed ?? DEFAULT_SEED,
  feather: params.feather ?? DEFAULT_FEATHER
});
var assertOptionalIntegerNumber = (value, name) => {
  if (value === undefined) {
    return;
  }
  if (!Number.isInteger(value)) {
    throw new TypeError(`"${name}" must be an integer, but got ${JSON.stringify(value)}`);
  }
};
var validatePixelDissolveParams = (params) => {
  assertEffectParamsObject(params, "Pixel Dissolve");
  assertOptionalFiniteNumber(params.progress, "progress");
  assertOptionalFiniteNumber(params.columns, "columns");
  assertOptionalFiniteNumber(params.rows, "rows");
  assertOptionalFiniteNumber(params.seed, "seed");
  assertOptionalFiniteNumber(params.feather, "feather");
  assertOptionalIntegerNumber(params.columns, "columns");
  assertOptionalIntegerNumber(params.rows, "rows");
  const r = resolve(params);
  validateUnitInterval(r.progress, "progress");
  validateUnitInterval(r.feather, "feather");
  if (r.columns < 1) {
    throw new TypeError(`"columns" must be >= 1, but got ${JSON.stringify(r.columns)}`);
  }
  if (r.rows < 1) {
    throw new TypeError(`"rows" must be >= 1, but got ${JSON.stringify(r.rows)}`);
  }
};
var VERTEX_SHADER = `#version 300 es
in vec2 aPos;
in vec2 aUv;
out vec2 vUv;

void main() {
	vUv = aUv;
	gl_Position = vec4(aPos, 0.0, 1.0);
}
`;
var FRAGMENT_SHADER = `#version 300 es
precision highp float;

in vec2 vUv;
out vec4 fragColor;

uniform sampler2D uSource;
uniform float uProgress;
uniform float uColumns;
uniform float uRows;
uniform float uSeed;
uniform float uFeather;

float hash21(vec2 p) {
	vec3 p3 = fract(vec3(p.xyx) * vec3(443.8975, 397.2973, 491.1871));
	p3 += dot(p3, p3.yzx + 19.19);
	return fract((p3.x + p3.y) * p3.z);
}

float dissolveMask(float noise, float progress, float feather) {
	float edge = max(feather, 0.0);
	float threshold = 1.0 - clamp(progress, 0.0, 1.0) * (1.0 + edge);

	if (edge <= 0.0001) {
		return 1.0 - step(threshold, noise);
	}

	return 1.0 - smoothstep(threshold, threshold + edge, noise);
}

void main() {
	vec2 divisions = max(vec2(uColumns, uRows), vec2(1.0));
	vec2 gridUv = min(vUv, vec2(1.0) - vec2(0.000001));
	// Stabilize cells when a pixel center falls exactly on a grid boundary.
	vec2 cell = floor(gridUv * divisions + vec2(0.00001));
	vec4 color = texture(uSource, vUv);

	float noise = hash21(cell + vec2(uSeed, uSeed * 1.618));
	float mask = dissolveMask(noise, uProgress, uFeather);

	fragColor = vec4(color.rgb * mask, color.a * mask);
}
`;
var compileShader = (gl, type, source) => {
  const shader = gl.createShader(type);
  if (!shader) {
    throw new Error("Failed to create WebGL shader");
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Pixel Dissolve shader compile failed: ${log ?? "(no log)"}`);
  }
  return shader;
};
var linkProgram = (gl, vs, fs) => {
  const program = gl.createProgram();
  if (!program) {
    throw new Error("Failed to create WebGL program");
  }
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(`Pixel Dissolve program link failed: ${log ?? "(no log)"}`);
  }
  return program;
};
var createFullscreenQuad = (gl) => {
  const vao = gl.createVertexArray();
  if (!vao) {
    throw new Error("Failed to create WebGL vertex array");
  }
  gl.bindVertexArray(vao);
  const vbo = gl.createBuffer();
  if (!vbo) {
    throw new Error("Failed to create WebGL buffer");
  }
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 0, 0, 1, -1, 1, 0, -1, 1, 0, 1, 1, 1, 1, 1]), gl.STATIC_DRAW);
  return { vao, vbo };
};
var pixelDissolve = createEffect({
  type: "dev.remotion.effects.pixelDissolve",
  label: "pixelDissolve()",
  documentationLink: "https://www.remotion.dev/docs/effects/pixel-dissolve",
  calculateKey: (params) => {
    const r = resolve(params);
    return `pixel-dissolve-${r.progress}-${r.columns}-${r.rows}-${r.seed}-${r.feather}`;
  },
  implementations: {
    webgpu: Internals.createEffectImplementation({
      setup: (target) => setupWebGpuSinglePass(target, "pixel-dissolve"),
      apply: ({ source, width, height, params, state, gpuDevice }) => {
        if (!gpuDevice)
          throw new Error("A WebGPU device is required for the pixel-dissolve effect");
        const r = resolve(params);
        const uniformData = new Float32Array([
          r.progress,
          r.columns,
          r.rows,
          r.seed,
          r.feather,
          0,
          0,
          0
        ]);
        applyWebGpuSinglePass({
          state,
          source,
          width,
          height,
          device: gpuDevice,
          shader: PIXEL_DISSOLVE_WEBGPU_SHADER,
          uniformData
        });
      },
      cleanup: cleanupWebGpuSinglePass
    }),
    webgl2: Internals.createEffectImplementation({
      setup: (target) => {
        const gl = target.getContext("webgl2", {
          premultipliedAlpha: true,
          alpha: true,
          preserveDrawingBuffer: true
        });
        if (!gl) {
          throw createWebGL2ContextError("pixel dissolve effect");
        }
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
        const vs = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
        const fs = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
        const program = linkProgram(gl, vs, fs);
        gl.deleteShader(vs);
        gl.deleteShader(fs);
        const { vao, vbo } = createFullscreenQuad(gl);
        const aPos = gl.getAttribLocation(program, "aPos");
        const aUv = gl.getAttribLocation(program, "aUv");
        gl.enableVertexAttribArray(aPos);
        gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 16, 0);
        gl.enableVertexAttribArray(aUv);
        gl.vertexAttribPointer(aUv, 2, gl.FLOAT, false, 16, 8);
        gl.bindVertexArray(null);
        const texture = gl.createTexture();
        if (!texture) {
          throw new Error("Failed to create WebGL texture");
        }
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.bindTexture(gl.TEXTURE_2D, null);
        return {
          gl,
          program,
          vao,
          vbo,
          texture,
          uSource: gl.getUniformLocation(program, "uSource"),
          uProgress: gl.getUniformLocation(program, "uProgress"),
          uColumns: gl.getUniformLocation(program, "uColumns"),
          uRows: gl.getUniformLocation(program, "uRows"),
          uSeed: gl.getUniformLocation(program, "uSeed"),
          uFeather: gl.getUniformLocation(program, "uFeather")
        };
      },
      apply: ({ source, width, height, params, state, flipSourceY }) => {
        const r = resolve(params);
        state.gl.viewport(0, 0, width, height);
        state.gl.bindFramebuffer(state.gl.FRAMEBUFFER, null);
        state.gl.clearColor(0, 0, 0, 0);
        state.gl.clear(state.gl.COLOR_BUFFER_BIT);
        state.gl.useProgram(state.program);
        state.gl.bindVertexArray(state.vao);
        state.gl.activeTexture(state.gl.TEXTURE0);
        state.gl.bindTexture(state.gl.TEXTURE_2D, state.texture);
        state.gl.pixelStorei(state.gl.UNPACK_FLIP_Y_WEBGL, flipSourceY);
        state.gl.texImage2D(state.gl.TEXTURE_2D, 0, state.gl.RGBA, state.gl.RGBA, state.gl.UNSIGNED_BYTE, source);
        if (state.uSource)
          state.gl.uniform1i(state.uSource, 0);
        if (state.uProgress)
          state.gl.uniform1f(state.uProgress, r.progress);
        if (state.uColumns)
          state.gl.uniform1f(state.uColumns, r.columns);
        if (state.uRows)
          state.gl.uniform1f(state.uRows, r.rows);
        if (state.uSeed)
          state.gl.uniform1f(state.uSeed, r.seed);
        if (state.uFeather)
          state.gl.uniform1f(state.uFeather, r.feather);
        state.gl.drawArrays(state.gl.TRIANGLE_STRIP, 0, 4);
        state.gl.bindVertexArray(null);
        state.gl.bindTexture(state.gl.TEXTURE_2D, null);
        state.gl.useProgram(null);
      },
      cleanup: ({ gl, program, vao, vbo, texture }) => {
        gl.deleteTexture(texture);
        gl.deleteBuffer(vbo);
        gl.deleteProgram(program);
        gl.deleteVertexArray(vao);
      }
    })
  },
  schema: pixelDissolveSchema,
  validateParams: validatePixelDissolveParams
});
export {
  pixelDissolve
};
